import React, { useMemo, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useMutation, useQuery } from 'react-query';
import { useNavigation } from '@react-navigation/native';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import { HelperText } from 'react-native-paper';
import { fetchMyProfile, updateProfile } from '../../apis/appsyncApis';
import Loading from '../../components/Loading';
import { useI18n } from '../../context/I18nContext';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import ScrollablePage from '../../components/ui/ScrollablePage';
import Container from '../../components/Container';
import { MutationUpdateProfileArgs } from '../../types/appsync-types';
import FormikInput from '../../components/formik/FormikInput';
import GradientButton from '../../components/GradientButton';
import { AppDrawerNavigationProp } from '../../components/DrawerMenuContent';

interface UpdateEmailForm {
  email: string;
}

const UpdateEmail = () => {
  const mp = useMixpanel();
  const navigation = useNavigation<AppDrawerNavigationProp>();
  const { I18n } = useI18n();
  const [isErrorState, setIsErrorState] = useState<boolean>(false);
  const result = useQuery(fetchMyProfile.id, fetchMyProfile);
  const data = result.data?.data?.getMyProfile;

  const validateSchema = useMemo(() => {
    return yup.object().shape({
      email: yup.string().email(I18n.t('validation.validEmail')),
    });
  }, [I18n]);

  const saveProfile = useMutation(
    async (form: MutationUpdateProfileArgs) => {
      await updateProfile(form);
      if (form.email) mp?.getPeople().set('$email', form.email);
    },
    {
      onSuccess: () => {
        result.refetch();
        navigation.goBack();
      },
      onError: () => {
        setIsErrorState(true);
      },
    },
  );

  if (!data) {
    return <Loading />;
  }
  return (
    <ScrollablePage
      summary={{
        title: I18n.t('updateEmail.summaryTitle'),
        description: I18n.t('updateEmail.summaryDescription'),
        icon: 'pencil',
      }}
    >
      <Formik
        initialValues={{ email: data.email ?? '' }}
        onSubmit={(form) => {
          setIsErrorState(false);
          saveProfile.mutate({
            firstname: data.firstname,
            lastname: data.lastname,
            email: form.email || null,
            organization: data.organization,
            phonenumber: data.phonenumber,
          });
        }}
        validationSchema={validateSchema}
      >
        {({ handleSubmit, isValid }: FormikProps<UpdateEmailForm>) => (
          <Container>
            <FormikInput
              name="email"
              /* There seems to be a bug on web with autofocus happening during a react navigation transition with a fixed side bar causing glitching */
              // https://github.com/react-navigation/react-navigation/issues/9875
              autoFocus={Platform.OS === 'web' ? undefined : true}
              label={I18n.t('updateEmail.email')}
              style={styles.input}
              mode={'outlined'}
              keyboardType={'email-address'}
              autoCapitalize={'none'}
              autoComplete={'email'}
            />
            <GradientButton
              style={{ marginBottom: 15 }}
              loading={saveProfile.isLoading}
              onPress={() => handleSubmit()}
              disabled={saveProfile.isLoading || !isValid}
            >
              {I18n.t('general.save')}
            </GradientButton>
            {isErrorState && <HelperText type="error">{I18n.t('general.unexpectedError')}</HelperText>}
          </Container>
        )}
      </Formik>
    </ScrollablePage>
  );
};

const styles = StyleSheet.create({
  input: {
    marginTop: 30,
  },
});

export default UpdateEmail;
